import React, { useEffect, useMemo, useState } from "react";
import "../assets/css/AddFollowup.css";
import axiosInstance from "../components/api/axiosInstance";
import { getAuthItem } from "../utils/authStorage";

function getLoggedInUserId() {
  try {
    const userStr = getAuthItem("user");
    if (!userStr) return "";
    const user = JSON.parse(userStr);
    const id = user.user_id ?? user.id ?? user.User_Id;
    return id != null ? String(id) : "";
  } catch {
    return "";
  }
}

function userLabel(u) {
  const name = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
  return name || u.email || `User #${u.user_id}`;
}

function AddFollowup({ id, type = "lead", onBack }) {
  const loggedInUserId = useMemo(() => getLoggedInUserId(), []);
  const [formData, setFormData] = useState({
    followupDate: "",
    followupType: "",
    nextFollowupDate: "",
    remarks: "",
    status: "Due",
    outcome: "",
    assignedTo: loggedInUserId,
  });
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        const response = await axiosInstance.get("/users", { params: { status: "Active" } });
        const list = Array.isArray(response.data) ? response.data : [];
        const mapped = list
          .map((u) => ({
            user_id: u.user_id ?? u.User_Id ?? u.id,
            first_name: u.first_name || "",
            last_name: u.last_name || "",
            email: u.email || "",
            status: u.status || "",
          }))
          .filter((u) => u.user_id != null)
          .sort((a, b) => userLabel(a).localeCompare(userLabel(b)));
        if (!cancelled) {
          setUsers(mapped);
          setFormData((prev) => {
            if (prev.assignedTo) return prev;
            return { ...prev, assignedTo: loggedInUserId || (mapped[0] ? String(mapped[0].user_id) : "") };
          });
        }
      } catch (err) {
        console.error("Error loading users:", err);
        if (!cancelled) setUsers([]);
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    };
    loadUsers();
    return () => {
      cancelled = true;
    };
  }, [loggedInUserId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.followupDate) {
      alert("Followup Date is required.");
      return;
    }

    const createdBy = Number(loggedInUserId) || 1;
    const assignedTo = formData.assignedTo ? Number(formData.assignedTo) : createdBy;

    setSubmitting(true);
    try {
      const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return null;
        const withSeconds = dateTimeString + ":00";
        if (type === "internship-lead" || type === "realestate-lead") {
          return withSeconds.replace("T", " ");
        }
        return withSeconds;
      };

      const payload = {
        Followup_Date: formatDateTime(formData.followupDate),
        Remarks: formData.remarks || null,
        Status: formData.status || "Due",
        Outcome: formData.outcome || null,
        Next_Followup_Date: formatDateTime(formData.nextFollowupDate),
        Created_By: createdBy,
        Assigned_To: assignedTo,
        Followup_Type: formData.followupType || null,
      };

      if (type === "lead") payload.Lead_Id = id;
      if (type === "client") payload.client_id = id;
      if (type === "training-lead") payload.Training_Lead_Id = id;
      if (type === "job-lead") payload.Job_Lead_Id = id;
      if (type === "internship-lead") payload.Internship_Lead_Id = id;
      if (type === "realestate-lead") payload.Real_Estate_Lead_Id = id;

      const endpoint =
        type === "training-lead"
          ? "/create-training-followup"
          : type === "job-lead"
            ? "/create-job-followup"
            : type === "internship-lead"
              ? "/create-internship-followup"
              : type === "realestate-lead"
                ? "/create-real-estate-followup"
                : "/create-followup";
      await axiosInstance.post(endpoint, payload);
      alert("Followup added successfully!");
      onBack();
    } catch (error) {
      console.error("Error adding followup:", error);
      const errorMsg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to add followup. Please try again.";
      alert(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    onBack();
  };

  return (
    <div className="addfollowup-container">
      <form className="addfollowup-form" onSubmit={handleSubmit}>
        <div className="addfollowup-form-grid">
          <div className="addfollowup-form-group">
            <label>
              Followup Date <span className="required">*</span>
            </label>
            <input
              type="datetime-local"
              name="followupDate"
              value={formData.followupDate}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="addfollowup-form-group">
            <label>Followup Type</label>
            <input
              type="text"
              name="followupType"
              value={formData.followupType}
              onChange={handleInputChange}
            />
          </div>

          <div className="addfollowup-form-group">
            <label>Next Followup Date</label>
            <input
              type="datetime-local"
              name="nextFollowupDate"
              value={formData.nextFollowupDate}
              onChange={handleInputChange}
            />
          </div>

          <div className="addfollowup-form-group">
            <label>
              Assigned To <span className="required">*</span>
            </label>
            <select
              name="assignedTo"
              value={formData.assignedTo}
              onChange={handleInputChange}
              required
              disabled={loadingUsers}
            >
              {loadingUsers && <option value="">Loading users...</option>}
              {!loadingUsers && users.length === 0 && (
                <option value={loggedInUserId || ""}>Current user</option>
              )}
              {users.map((u) => (
                <option key={u.user_id} value={String(u.user_id)}>
                  {userLabel(u)}
                </option>
              ))}
            </select>
          </div>

          <div className="addfollowup-form-group">
            <label>Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
            >
              <option value="Due">Due</option>
              <option value="Done">Done</option>
              <option value="Rescheduled">Rescheduled</option>
              <option value="No Response">No Response</option>
            </select>
          </div>

          <div className="addfollowup-form-group">
            <label>Outcome</label>
            <input
              type="text"
              name="outcome"
              value={formData.outcome}
              onChange={handleInputChange}
              placeholder="Short call/meeting result"
            />
          </div>
        </div>

        <div className="addfollowup-remarks-group">
          <label>Remarks</label>
          <textarea
            name="remarks"
            value={formData.remarks}
            onChange={handleInputChange}
            rows={5}
          />
        </div>

        <div className="addfollowup-actions">
          <button
            type="submit"
            className="btn-addfollowup-submit"
            disabled={submitting}
          >
            {submitting ? "Adding..." : "New Followup"}
          </button>
          <button
            type="button"
            className="btn-addfollowup-cancel"
            onClick={handleCancel}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddFollowup;
